import { useState } from 'react';
import { flushSync } from 'react-dom';
import { RadixDialog, RadixDialogContent, RadixDialogHeader, RadixDialogTitle, RadixDialogDescription } from '../../vendor/ds-react/components/ui/dialog';
import { RadixButton } from '../../vendor/ds-react/components/ui/button';
import { useFullState, useCurrentStateAsRef, useWriteContext } from '../../vendor/src/features/remotion-editor/editor/utils/use-context';
import type { EditorState } from '../../vendor/src/features/remotion-editor/editor/state/types';
import { planGroupVoiceWindow, type GroupVoiceWindowPlan } from './group-voice-window';
type Confirmation = {
    base: EditorState['undoableState'];
    plan: Extract<GroupVoiceWindowPlan, {
        kind: 'ready';
    }>;
};
export function GroupVoiceWindowControl({ clipId, disabled = false }: {
    clipId: string | null;
    disabled?: boolean;
}) {
    const state = useFullState(), current = useCurrentStateAsRef(), write = useWriteContext(), [confirmation, setConfirmation] = useState<Confirmation | null>(null), [error, setError] = useState('');
    const plan = clipId ? planGroupVoiceWindow(state, clipId) : { kind: 'none' as const };
    if (plan.kind === 'none' && !confirmation)
        return null;
    return <><button type="button" disabled={disabled || plan.kind !== 'ready'} title={plan.kind === 'blocked' ? plan.reason : undefined} onClick={() => { if (plan.kind === 'ready') {
        setError('');
        setConfirmation({ base: state.undoableState, plan });
    } }}>그룹 구간에 맞춰 자르기</button>
 {plan.kind === 'blocked' && <p role="status">{plan.reason}</p>}
 {confirmation && <RadixDialog open onOpenChange={open => { if (!open)
            setConfirmation(null); }}><RadixDialogContent style={{ width: 520, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
 <RadixDialogHeader><RadixDialogTitle>그룹 안의 음성 구간으로 맞추기</RadixDialogTitle><RadixDialogDescription>그룹 밖으로 가려진 앞뒤 구간만 클립에서 제외합니다. 원본 파일·배속·페이드는 유지하며 실행 취소로 되돌릴 수 있습니다.</RadixDialogDescription></RadixDialogHeader>
 <p className="break-words">{confirmation.plan.filename}</p><p>클립 {((confirmation.plan.oldTo - confirmation.plan.oldFrom) / confirmation.plan.fps).toFixed(2)}초 → 그룹 안 {((confirmation.plan.to - confirmation.plan.from) / confirmation.plan.fps).toFixed(2)}초</p>
 <p>타임라인 {(confirmation.plan.oldFrom / confirmation.plan.fps).toFixed(2)}–{(confirmation.plan.oldTo / confirmation.plan.fps).toFixed(2)}초 → {(confirmation.plan.from / confirmation.plan.fps).toFixed(2)}–{(confirmation.plan.to / confirmation.plan.fps).toFixed(2)}초</p>
 <p>적용 후 문구를 확인하고 음성을 교체할 수 있습니다. 부모나 다른 멤버의 구간은 바꾸지 않습니다.</p>
 {error && <p role="alert">{error}</p>}
 <div className="flex justify-end gap-2"><RadixButton variant="outline" onClick={() => setConfirmation(null)}>취소</RadixButton>
 <RadixButton disabled={disabled} onClick={() => {
                if (current.current.undoableState !== confirmation.base) {
                    setError('편집 내용이 바뀌었습니다. 창을 닫고 구간을 다시 확인해 주세요.');
                    return;
                }
                const checked = planGroupVoiceWindow(current.current, confirmation.plan.clipId);
                if (checked.kind !== 'ready') {
                    setError(checked.kind === 'blocked' ? checked.reason : '구간이 바뀌었습니다. 창을 닫고 다시 확인해 주세요.');
                    return;
                }
                let applied = false, reason = '편집 내용이 바뀌었습니다. 구간을 다시 확인해 주세요.';
                flushSync(() => write.setState({ expectedUndoableState: confirmation.base, groupPropertyEdit: { type: 'voice-window', clipId: confirmation.plan.clipId }, commitToUndoStack: true, update: value => {
                        if (value.undoableState !== confirmation.base)
                            return value;
                        const next = planGroupVoiceWindow(value, confirmation.plan.clipId);
                        if (next.kind !== 'ready') {
                            if (next.kind === 'blocked')
                                reason = next.reason;
                            return value;
                        }
                        applied = true;
                        return next.state;
                    } }));
                if (applied)
                    setConfirmation(null);
                else
                    setError(reason);
            }}>이 구간으로 자르기</RadixButton></div></RadixDialogContent></RadixDialog>}
 </>;
}
